import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import {
  Country,
  State,
  LocationResponse,
  Warehouse,
  Vendor,
  Prospect,
  UserProfile,
  PrimaryMetricsResponse,
  SalesPipelineResponse,
  ProductCompanyLocationsResponse,
  ProductCompanyLocation,
  ProductCompanyProduct,
  SupplyHubMapResponse,
  FrontsiteClientsResponse,
  FrontsiteOrder,
  FrontsiteOrderItem,
} from '../types';
import { API_URL, APP_URL, GLOBAL_API_URL, FINANCE_REPORTS_URL, SALES_INTELLIGENCE_URL } from '../env';
import { getCookie } from '../utils/storage';

const getAuthCookie = (): string | null => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.startsWith('accessToken=')) {
      return cookie.substring('accessToken='.length);
    }
  }
  return null;
};

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

const salesIntelligenceApi = axios.create({
  baseURL: import.meta.env.DEV ? '/sales-intelligence' : SALES_INTELLIGENCE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

salesIntelligenceApi.interceptors.request.use((config) => {
  const cookieToken = getCookie('accessToken');
  if (cookieToken) {
    config.headers.set('Authorization', `Bearer ${cookieToken}`);
  }
  return config;
});

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const authCookie = getAuthCookie();
    if (!authCookie) {
      window.location.href = `${APP_URL}/login`;
      return Promise.reject('No access token');
    }

    config.headers.set('Authorization', `Bearer ${authCookie}`);

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 403 || error.response?.status === 401) {
      window.location.href = `${APP_URL}/error_403`;
    }
    return Promise.reject(error);
  }
);

export const fetchCountries = async (): Promise<Country[]> => {
  const response = await api.get('/api/countries/');
  // API wraps the array in a `data` envelope: { data: [...] }
  const data = response.data?.data ?? response.data ?? [];
  return Array.isArray(data) ? data : [data];
};

export const fetchStates = async (country: string): Promise<State[]> => {
  const response = await api.get(`/api/states/?country=${encodeURIComponent(country)}`);
  const states = response.data || [];
  
  // Clean and deduplicate states
  const cleanedStates = states
    .flatMap((state: string) => {
      // Split states that contain multiple states
      const multiStates = state
        .split(/[;/]/)
        .map((s: string) => s.trim())
        .filter((s: string) => s.length > 0);
      return multiStates;
    })
    .map((state: string) => {
      // Clean up each state name
      return state
        .replace(/,$/, '') // Remove trailing comma
        .trim()
        .replace(/\s+/g, ' '); // Normalize spaces
    })
    .filter((state: string) => {
      // Filter out invalid states
      return (
        state.length > 0 &&
        state !== 'NULL' &&
        !state.includes('Store') &&
        !state.match(/^[a-z]+$/) // Remove lowercase-only states like "mp"
      );
    })
    // Remove duplicates and sort
    .filter((state: string, index: number, array: string[]) => array.indexOf(state) === index)
    .sort();

  return cleanedStates;
};

export const fetchCities = async (region: string, country: string): Promise<string[]> => {
  const response = await api.post('/api/cities/', {
    region,
    country
  });
  return response.data || [];
};

export const fetchLocations = async (
  productFilter?: string,
  country?: string,
  state?: string,
  staff_id?: string,
  city?: string,
  location?: string,
  startDate?: string,
  endDate?: string
): Promise<LocationResponse> => {
  const params = new URLSearchParams();
  if (productFilter) params.append('product', productFilter);
  if (country) params.append('country', country);
  if (state) params.append('state', state);
  if (staff_id) params.append('staff_id', staff_id);
  if (city) params.append('city', city);
  if (location) params.append('location', location);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  
  const url = `/api/locations/${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await api.get(url);
  return response.data || { locations: [], total_amount: 0 };
};

const parseNumericValue = (value: any): number => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  const numeric = parseFloat(String(value).replace(/[^0-9.-]+/g, ''));
  return Number.isFinite(numeric) ? numeric : 0;
};

const parseFrontsiteItems = (rawItems: any): FrontsiteOrderItem[] => {
  if (!rawItems) {
    return [];
  }

  const resolveRawValue = (value: any): any => {
    let current = value;
    let depth = 0;
    while (typeof current === 'string' && depth < 5) {
      const trimmed = current.trim();
      const looksLikeJson =
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
        (trimmed.startsWith('"') && trimmed.endsWith('"'));

      if (!looksLikeJson) {
        break;
      }

      try {
        current = JSON.parse(trimmed);
        depth += 1;
      } catch {
        break;
      }
    }
    return current;
  };

  let parsed: any = resolveRawValue(rawItems);
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }

  const collections: any[] = [];
  if (Array.isArray(parsed)) {
    collections.push(...parsed);
  } else if (parsed && typeof parsed === 'object') {
    Object.values(parsed).forEach((value) => {
      if (Array.isArray(value)) {
        collections.push(...value);
      } else if (value && typeof value === 'object') {
        collections.push(value);
      }
    });
  }

  return collections
    .map((item) => ({
      id: item?.id ? String(item.id) : undefined,
      sku: item?.sku ? String(item.sku) : undefined,
      name: item?.name ? String(item.name) : undefined,
      qty: parseNumericValue(item?.qty),
      unit: item?.unit != null ? String(item.unit) : null,
      price: parseNumericValue(item?.price),
      subtotal: parseNumericValue(item?.subtotal),
      lot_no: item?.lot_no != null ? String(item.lot_no) : null,
      sold_by: item?.sold_by != null ? String(item.sold_by) : null,
      packsize: item?.packsize != null ? String(item.packsize) : null,
      packtype: item?.packtype != null ? String(item.packtype) : null,
      slug_url: item?.slug_url != null ? String(item.slug_url) : null,
      vendor_id: item?.vendor_id != null ? String(item.vendor_id) : null,
      order_type: item?.order_type != null ? String(item.order_type) : null,
      product_id: item?.product_id != null ? String(item.product_id) : null,
      inventoryid: item?.inventoryid != null ? String(item.inventoryid) : null,
      vendor_name: item?.vendor_name != null ? String(item.vendor_name) : null,
      warehouse_id: item?.warehouse_id != null ? String(item.warehouse_id) : null,
      warehouse_stock: item?.warehouse_stock != null ? String(item.warehouse_stock) : null,
      vendor_product_id: item?.vendor_product_id != null ? String(item.vendor_product_id) : null,
    }))
    .filter((item) => item.qty > 0 || item.subtotal > 0 || (item.name && item.name.trim().length > 0));
};

export const fetchFrontsiteClients = async ({
  country,
  state,
  city,
  startDate,
  endDate,
  searchTerm,
  productSearch,
}: {
  country?: string;
  state?: string;
  city?: string;
  startDate?: string;
  endDate?: string;
  searchTerm?: string;
  productSearch?: string;
} = {}): Promise<FrontsiteClientsResponse> => {
  const params = new URLSearchParams();
  if (country) params.append('country', country);
  if (state) params.append('state', state);
  if (city) params.append('city', city);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (searchTerm) params.append('search', searchTerm);
  if (productSearch) params.append('product', productSearch);

  const query = params.toString();
  const url = `/api/frontsite-clients/${query ? `?${query}` : ''}`;
  const response = await api.get(url);
  const data = response.data || {};

  const orders: FrontsiteOrder[] = Array.isArray(data.orders)
    ? data.orders.map((order: any): FrontsiteOrder => {
        const lonRaw = Array.isArray(order?.coordinates) ? order.coordinates[0] : null;
        const latRaw = Array.isArray(order?.coordinates) ? order.coordinates[1] : null;
        const lon = Number(lonRaw);
        const lat = Number(latRaw);
        const hasCoordinates = Number.isFinite(lon) && Number.isFinite(lat);

        const normalizedId = Number(order?.order_id ?? order?.id ?? 0);

        return {
          id: Number(order?.id ?? normalizedId) || normalizedId || 0,
          order_id: normalizedId || 0,
          userid: order?.userid ?? order?.customer_id,
          customer_id: order?.customer_id ? String(order.customer_id) : undefined,
          company: order?.company ? String(order.company) : undefined,
          so_number: order?.so_number ? String(order.so_number) : undefined,
          total: parseNumericValue(order?.total),
          subtotal: parseNumericValue(order?.subtotal),
          shipping_charge: parseNumericValue(order?.shipping_charge),
          payment_status: order?.payment_status ? String(order.payment_status) : undefined,
          status: order?.status ? String(order.status) : undefined,
          created_at: order?.created_at ? String(order.created_at) : undefined,
          updated_at: order?.updated_at ? String(order.updated_at) : undefined,
          email: order?.email ? String(order.email) : undefined,
          phonenumber: order?.phonenumber ? String(order.phonenumber) : undefined,
          sales_representative: order?.sales_representative ? String(order.sales_representative) : undefined,
          staff_id: order?.staff_id != null ? String(order.staff_id) : undefined,
          so_id: order?.so_id != null ? String(order.so_id) : undefined,
          ship_status: order?.ship_status ? String(order.ship_status) : undefined,
          shipping_correction_id:
            order?.shipping_correction_id != null ? String(order.shipping_correction_id) : undefined,
          address: order?.address ? String(order.address) : undefined,
          city: order?.city ? String(order.city) : undefined,
          state: order?.state ? String(order.state) : undefined,
          zip: order?.zip ? String(order.zip) : undefined,
          country: order?.country || null,
          coordinates: hasCoordinates ? [lon as number, lat as number] : null,
          client_total_amount: parseNumericValue(order?.client_total_amount),
          target: parseNumericValue(order?.target),
          target_year: order?.target_year != null ? Number(order.target_year) : undefined,
          target_achievement: parseNumericValue(order?.target_achievement),
          items: parseFrontsiteItems(order?.items),
        };
      })
    : [];

  return {
    total_amount: parseNumericValue(data?.total_amount),
    total_target: parseNumericValue(data?.total_target),
    target_year: data?.target_year != null ? Number(data.target_year) : undefined,
    overall_target_achievement: parseNumericValue(data?.overall_target_achievement),
    orders,
  };
};

export const fetchProductCompanyLocations = async (
  params: {
    country?: string;
    state?: string;
    city?: string;
    product?: string;
    search?: string;
    page?: number;
    pageSize?: number;
    year?: number;
    startDate?: string;
    endDate?: string;
  } = {}
): Promise<ProductCompanyLocationsResponse> => {
  const searchParams = new URLSearchParams();
  if (params.country) searchParams.append('country', params.country);
  if (params.state) searchParams.append('state', params.state);
  if (params.city) searchParams.append('city', params.city);
  if (params.product) searchParams.append('product', params.product);
  if (params.search) searchParams.append('search', params.search);
  if (params.page) searchParams.append('page', String(params.page));
  if (params.pageSize) searchParams.append('page_size', String(params.pageSize));
  if (params.year) searchParams.append('year', String(params.year));
  if (params.startDate) searchParams.append('start_date', params.startDate);
  if (params.endDate) searchParams.append('end_date', params.endDate);

  const url = `/api/product-company-locations/${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  const response = await api.get(url);
  const data = response.data || { total_companies: 0, locations: [] };
  const rawLocations: any[] = Array.isArray(data.locations) ? data.locations : [];

  const locations: ProductCompanyLocation[] = rawLocations.map((location) => {
    const latitude = Number(location.latitude);
    const longitude = Number(location.longitude);
    const products: ProductCompanyProduct[] = Array.isArray(location.products)
      ? location.products.map((product: any) => ({
          product_name: String(product.product_name ?? ''),
          quantity: Number(product.quantity) || 0,
          rate: Number(product.rate) || 0,
          unit: product.unit != null ? String(product.unit).trim() || null : null,
          target: Number(product.target) || 0,
        }))
      : [];

    return {
      userid: String(location.userid ?? ''),
      company: String(location.company ?? ''),
      latitude: Number.isFinite(latitude) ? latitude : 0,
      longitude: Number.isFinite(longitude) ? longitude : 0,
      city: String(location.city ?? ''),
      state: String(location.state ?? ''),
      country: String(location.country ?? ''),
      products,
    };
  });

  return {
    total_companies: Number(data.total_companies) || locations.length,
    locations,
  };
};

export const fetchWarehouses = async (
  country?: string,
  state?: string
): Promise<Warehouse[]> => {
  const params = new URLSearchParams();
  if (country) params.append('country', country);
  if (state) params.append('state', state);
  
  const url = `/api/warehouses/${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await api.get(url);
  const data = response.data || [];
  // Ensure we return an array even if the API response is a single object
  return Array.isArray(data) ? data : [data];
};

export const fetchSupplyHubMap = async (
  country?: string,
  state?: string,
  staffId?: string,
  startDate?: string,
  endDate?: string,
): Promise<SupplyHubMapResponse> => {
  const params = new URLSearchParams();
  if (country) params.append('country', country);
  if (state) params.append('state', state);
  if (staffId) params.append('staff_id', staffId);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  const query = params.toString();
  const url = `/api/supply-hub-map/${query ? `?${query}` : ''}`;
  const response = await api.get(url);
  const data = response.data;
  if (!data || typeof data !== 'object') {
    return {
      total_warehouses: 0,
      total_purchase_orders: 0,
      warehouses: [],
    };
  }

  return {
    total_warehouses: Number(data.total_warehouses) || 0,
    total_purchase_orders: Number(data.total_purchase_orders) || 0,
    warehouses: Array.isArray(data.warehouses) ? data.warehouses : [],
  };
};

export const fetchVendors = async (
  productFilter?: string,
  country?: string,
  state?: string,
  city?: string,
  startDate?: string,
  endDate?: string
): Promise<Vendor[]> => {
  const params = new URLSearchParams();
  if (productFilter) params.append('product', productFilter);
  if (country) params.append('country', country);
  if (state) params.append('state', state);
  if (city) params.append('city', city);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  
  const url = `/api/vendors/${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await api.get(url);
  return response.data || [];
};

export const fetchProspects = async (
  country?: string,
  state?: string,
  staff_id?: string,
  city?: string,
  productFilter?: string,
  startDate?: string,
  endDate?: string
): Promise<Prospect[]> => {
  const params = new URLSearchParams();
  if (country) params.append('country', country);
  if (state) params.append('state', state);
  if (staff_id) params.append('staff_id', staff_id);
  if (city) params.append('city', city);
  if (productFilter) params.append('product', productFilter);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  
  const url = `/api/prospects/${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await api.get(url);
  return response.data || [];
};

export interface ClientOverviewEntityStats {
  with_coordinates: number;
  without_coordinates: number;
  total: number;
}

export interface ClientOverviewSummary {
  total_with_coordinates: number;
  total_without_coordinates: number;
  grand_total: number;
}

export interface ClientOverviewResponse {
  clients: ClientOverviewEntityStats;
  vendors: ClientOverviewEntityStats;
  prospects: ClientOverviewEntityStats;
  summary: ClientOverviewSummary;
}

export const fetchClientOverview = async (params: {
  country?: string;
  state?: string;
  city?: string;
  startDate?: string;
  endDate?: string;
} = {}): Promise<ClientOverviewResponse> => {
  const searchParams = new URLSearchParams();
  if (params.country) searchParams.append('country', params.country);
  if (params.state) searchParams.append('state', params.state);
  if (params.city) searchParams.append('city', params.city);
  if (params.startDate) searchParams.append('start_date', params.startDate);
  if (params.endDate) searchParams.append('end_date', params.endDate);
  const url = `/api/client-overview-list/${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  const response = await api.get(url);
  return response.data;
};

export type MismatchedEntityType = 'clients' | 'vendors' | 'prospects';

export interface MismatchedLocation {
  id: string;
  userid?: string;
  company?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: {
    country_id?: number;
    short_name?: string;
    long_name?: string;
  } | null;
  coordinates?: [number, number] | null;
  staff_name?: string;
  staff_id?: string;
  address_matched?: string | null;
  website_validation_notes?: string;
  website_found_address?: string;
  website_found_city?: string;
  website_found_state?: string;
  website_found_country?: string;
  is_validated?: number;
}

export interface MismatchedLocationsResponse {
  entity_type: MismatchedEntityType;
  total_count: number;
  locations: MismatchedLocation[];
}

export const fetchMismatchedLocations = async ({
  type,
  country,
  state,
  city,
  startDate,
  endDate,
}: {
  type: MismatchedEntityType;
  country?: string;
  state?: string;
  city?: string;
  startDate?: string;
  endDate?: string;
}): Promise<MismatchedLocationsResponse> => {
  const params = new URLSearchParams();
  params.append('type', type);
  if (country) params.append('country', country);
  if (state) params.append('state', state);
  if (city) params.append('city', city);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  const url = `/api/get-mismatched-locations?${params.toString()}`;
  const response = await api.get(url);
  return response.data;
};

// Cache for user profile to avoid repeated API calls
let userProfileCache: any = null;

export const fetchSalesReps = async (
  search?: string,
  page: number = 1
): Promise<{ 
  data: { staffid: string; alias_name: string }[]; 
  totalCount: number; 
  nextPage: number | null;
}> => {
  const authCookie = getAuthCookie();
  if (!authCookie) {
    throw new Error('No access token found');
  }

  // Get the user profile if not already cached
  if (!userProfileCache) {
    try {
      const profileResponse = await axios.post(
        `${GLOBAL_API_URL}/api/v1/token/verify/`,
        { token: authCookie },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      userProfileCache = profileResponse.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Continue without the user profile
    }
  }

  // Call the global API directly with reporting_to parameter if user_id is available
  const params: any = { 
    per_page: 10,
    page,
    status: 1,
    search: search || ''
  };

  // Add reporting_to parameter if user_id is available and user is not admin
  if (userProfileCache && userProfileCache.user_id && !userProfileCache.is_admin) {
    params.reporting_to = userProfileCache.user_id;
  }

  // Add company_id parameter from user profile
  if (userProfileCache && userProfileCache.company_id) {
    params.company_id = userProfileCache.company_id;
  }

  const response = await axios.get(
    `${GLOBAL_API_URL}/api/v1/usersofcompany/get/`, 
    {
      params,
      headers: {
        'Authorization': `Bearer ${authCookie}`
      }
    }
  );
  
  const responseData = response.data;
  
  // Extract next page number from the next URL if it exists
  let nextPage = null;
  if (responseData.next) {
    const nextUrl = new URL(responseData.next);
    const nextPageParam = nextUrl.searchParams.get('page');
    if (nextPageParam) {
      nextPage = parseInt(nextPageParam);
    }
  }
  
  // Transform the response to match the expected format
  const transformedData = (responseData.results || []).map((user: any) => ({
    staffid: user.id, // Use the encrypted ID
    alias_name: user.alias_name || 
      (user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : 
       `User ID: ${user.id.substring(0, 8)}...`)
  }));
  
  return {
    data: transformedData,
    totalCount: responseData.count || 0,
    nextPage
  };
};

export const fetchClientProducts = async (clientId: string): Promise<{
  products: Array<{
    product_name: string;
    short_description: string;
    quantity: number;
    order_date: string;
    order_id: number;
    client_name: string;
  }>;
  total_count: number;
}> => {
  const response = await api.get('/api/client-products/', {
    params: { client_id: clientId }
  });
  return response.data;
};

export const fetchClientOrders = async (clientId: string): Promise<{
  orders: Array<{
    order_id: number;
    order_date: string;
    total_amount: number;
    status: string;
    client_name: string;
  }>;
  total_count: number;
}> => {
  const response = await api.get('/api/client-orders/', {
    params: { client_id: clientId }
  });
  return response.data;
};

export const fetchUserProfile = async (): Promise<UserProfile> => {
  const authCookie = getAuthCookie();
  if (!authCookie) {
    throw new Error('No access token found');
  }

  const response = await axios.post(`${GLOBAL_API_URL}/api/v1/token/verify/`, {
    token: authCookie
  }, {
    headers: {
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

export const logout = async (): Promise<void> => {
  document.cookie = 'accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  window.location.href = `${APP_URL}/login`;
};

// Diagnosys API functions
export const fetchDiagnosysData = async (
  page: number = 1,
  pageSize: number = 5,
  country?: string,
  state?: string,
  search?: string,
  status?: string
): Promise<{
  summary: {
    totalRecords: number;
    correctLocations: number;
    incorrectLocations: number;
    accuracyPercentage: number;
    lastUpdated: string;
  };
  records: Array<{
    id: string;
    name: string;
    country: string;
    latitude: number;
    longitude: number;
    isCorrect: boolean;
    currentLocation: {
      country: string;
      state: string;
      city: string;
      address: string;
    };
    correctLocation: {
      country: string;
      state: string;
      city: string;
      address: string;
    };
    discrepancy?: {
      type: string;
      description: string;
      distance: string;
      severity: string;
    };
    confidence: number;
    lastUpdated: string;
    source: string;
    validationMethod: string;
  }>;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalRecords: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}> => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  if (country) params.append('country', country);
  if (state) params.append('state', state);
  if (search) params.append('search', search);
  if (status) params.append('status', status);

  const response = await api.get(`/api/diagnosys/locations/?${params.toString()}`);
  return response.data.data;
};

export const fetchDiagnosysLocationDetails = async (locationId: string): Promise<any> => {
  const response = await api.get(`/api/diagnosys/locations/${locationId}/`);
  return response.data.data;
};

export const correctDiagnosysLocation = async (
  locationId: string,
  correctionData: {
    correctionType: 'auto_correct' | 'manual_override';
    newLocation: {
      country: string;
      state: string;
      city: string;
      address: string;
    };
    reason: string;
    userConfirmed: boolean;
  }
): Promise<any> => {
  const response = await api.put(`/api/diagnosys/locations/${locationId}/correct/`, correctionData);
  return response.data.data;
};


export const exportDiagnosysReport = async (
  format: 'csv' | 'xlsx' | 'pdf',
  filters?: {
    country?: string;
    state?: string;
    status?: string;
    includeCorrect?: boolean;
    includeIncorrect?: boolean;
  }
): Promise<Blob> => {
  const params = new URLSearchParams();
  params.append('format', format);
  if (filters?.country) params.append('country', filters.country);
  if (filters?.state) params.append('state', filters.state);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.includeCorrect !== undefined) params.append('includeCorrect', filters.includeCorrect.toString());
  if (filters?.includeIncorrect !== undefined) params.append('includeIncorrect', filters.includeIncorrect.toString());

  const response = await api.get(`/api/diagnosys/export/?${params.toString()}`, {
    responseType: 'blob'
  });
  return response.data;
};

export const fetchDiagnosysStatistics = async (
  filters?: {
    country?: string;
    state?: string;
    dateRange?: '7d' | '30d' | '90d' | 'all';
  }
): Promise<any> => {
  const params = new URLSearchParams();
  if (filters?.country) params.append('country', filters.country);
  if (filters?.state) params.append('state', filters.state);
  if (filters?.dateRange) params.append('dateRange', filters.dateRange);

  const response = await api.get(`/api/diagnosys/statistics/?${params.toString()}`);
  return response.data.data;
};

export const clearDiagnosysCache = async (): Promise<{
  status: string;
  message: string;
}> => {
  const response = await api.post('/api/diagnosys/cache/clear/');
  return response.data;
};

export const fetchClientSummary = async (
  partnerIds: number[]
): Promise<{
  success: boolean;
  data: Array<{
    partner_id: number;
    client_name: string;
    opening_balance: number;
    total_debited: number;
    total_credited: number;
    closing_balance: number;
    formatted_opening_balance: string;
    formatted_total_debited: string;
    formatted_total_credited: string;
    formatted_closing_balance: string;
  }>;
  meta: {
    total_clients: number;
    date_range: {
      from_date: string | null;
      to_date: string | null;
    };
  };
}> => {
  const token = getAuthCookie(); //gitleaks:allow
  if (!token) {
    throw new Error('No access token found');
  }

  // Get user profile to extract company_id (already encrypted; sent through as-is)
  const userProfile = await fetchUserProfile();
  const companyId = userProfile.company_id;

  const response = await axios.post(
    `${FINANCE_REPORTS_URL}/api/get-clients-summary`,
    {
      company_id: companyId,
      partner_ids: partnerIds
    },
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
};

export const fetchSalesPipeline = async (
  params: {
    country?: string;
    state?: string;
    city?: string;
    salesRep?: string;
    startDate?: string;
    endDate?: string;
    productSearch?: string;
  } = {}
): Promise<SalesPipelineResponse> => {
  const searchParams = new URLSearchParams();
  if (params.country) searchParams.append('country', params.country);
  if (params.state) searchParams.append('state', params.state);
  if (params.city) searchParams.append('city', params.city);
  if (params.salesRep) searchParams.append('sales_rep', params.salesRep);
  if (params.startDate) searchParams.append('start_date', params.startDate);
  if (params.endDate) searchParams.append('end_date', params.endDate);
  if (params.productSearch) searchParams.append('product_search', params.productSearch);

  const url = `/api/sales-pipeline/${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  const response = await api.get(url);
  return response.data || {
    total_pipeline_value: 0,
    stage_summary: {},
    total_clients: 0,
    pipeline_data: []
  };
};

export const fetchCustomerInsights = async (params: {
  customerId: string | number;
  dateFrom: string;
  dateTo: string;
  vmi?: string;
  values?: boolean;
}) => {
  const searchParams = new URLSearchParams({
    customer_id: String(params.customerId),
    date_from: params.dateFrom,
    date_to: params.dateTo,
  });
  if (params.vmi) searchParams.set('vmi', params.vmi);
  if (params.values) searchParams.set('values', 'true');

  const url = `/api/reports/executive-finance-direct/sales-invoices/customers/details?${searchParams.toString()}`;
  const response = await salesIntelligenceApi.get(url);
  return response.data;
};

export const fetchCustomerKpis = async (params: {
  customerId: string | number;
  dateFrom: string;
  dateTo: string;
  mode?: string;
  range?: string;
  vmi?: string;
}) => {
  const searchParams = new URLSearchParams({
    customer_id: String(params.customerId),
    date_from: params.dateFrom,
    date_to: params.dateTo,
  });
  if (params.mode) searchParams.set('mode', params.mode);
  if (params.range) searchParams.set('range', params.range);
  if (params.vmi) searchParams.set('vmi', params.vmi);

  const url = `/api/reports/executive-finance-direct/sales-invoices/customers/kpis?${searchParams.toString()}`;
  const response = await salesIntelligenceApi.get(url);
  return response.data;
};

export const fetchCustomerRevenueTrend = async (params: {
  customerId: string | number;
  dateFrom: string;
  dateTo: string;
  range: string;
  vmi?: string;
}) => {
  const searchParams = new URLSearchParams({
    customer_id: String(params.customerId),
    date_from: params.dateFrom,
    date_to: params.dateTo,
    range: params.range,
  });
  if (params.vmi) searchParams.set('vmi', params.vmi);

  const url = `/api/reports/executive-finance-direct/sales-invoices/customers/revenue-trend?${searchParams.toString()}`;
  const response = await salesIntelligenceApi.get(url);
  return response.data;
};

export const fetchCustomerListings = async (params: {
  listingType: string;
  customerId: string | number;
  dateFrom: string;
  dateTo: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}) => {
  const searchParams = new URLSearchParams({
    listing_type: params.listingType,
    customer_id: String(params.customerId),
    date_from: params.dateFrom,
    date_to: params.dateTo,
    limit: String(params.limit ?? 25),
    offset: String(params.offset ?? 0),
  });
  if (params.sortBy) searchParams.set('sort_by', params.sortBy);
  if (params.sortOrder) searchParams.set('sort_order', params.sortOrder);

  const url = `/api/reports/executive-finance-direct/sales-invoices/listings?${searchParams.toString()}`;
  const response = await salesIntelligenceApi.get(url);
  return response.data;
};

export const fetchPrimaryMetrics = async (
  country?: string,
  state?: string,
  city?: string,
  dateRange?: string,
  startDate?: string,
  endDate?: string
): Promise<PrimaryMetricsResponse> => {
  const params = new URLSearchParams();
  if (country) params.append('country', country);
  if (state) params.append('state', state);
  if (city) params.append('city', city);
  if (dateRange) params.append('dateRange', dateRange);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  
  const url = `/api/primary-metrics${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await api.get(url);
  return response.data;
};

export default api;
